// Pure functions turning a workflow's routes[] into React Flow nodes/edges.
// Read-only: never mutates the input workflow.

const GAP_X = 40;
const GAP_Y = 80;

// When a block has at least this many route children, render them as a single
// vertical list node instead of a wide horizontal row.
const LIST_MIN = 5;

const NODE_W = {
  specialty: 230,
  default: 300,
  group: 230,
  flag: 210,
  route: 300,
  list: 360,
};

const RESOURCE_DEFS = [
  ["soap_note_template", "NOTE"],
  ["conversation_prompt", "CONV"],
  ["secondary_prompt", "2ND"],
];

export function extractResources(resources = {}) {
  return RESOURCE_DEFS.filter(
    ([key]) => resources?.[key]?.id && resources[key].id !== "none"
  ).map(([key, label]) => ({ key, label, id: resources[key].id }));
}

function resourceId(resources, key) {
  const id = resources?.[key]?.id;
  return id && id !== "none" ? id : "";
}

// Mirrors note_workflows.pick_first_full_match_names_versions: a sparse winner
// (missing soap_note OR conversation_prompt) takes the whole default_route set,
// not a per-key merge.
export function resolveMatchResources(routeResources, defaultResources) {
  const noteId = resourceId(routeResources, "soap_note_template");
  const convId = resourceId(routeResources, "conversation_prompt");
  if (!(noteId && convId)) {
    return extractResources(defaultResources);
  }
  return extractResources(routeResources);
}

function estimateRouteHeight(data) {
  const providerLines = data.anyProvider ? 1 : data.providers.length;
  return 58 + providerLines * 18 + (data.resources.length ? 10 + data.resources.length * 22 : 0);
}

function estimateDefaultHeight(resources) {
  return 62 + resources.length * 22;
}

// ---------------------------------------------------------------------------
// Grouping: routes -> top-layer blocks per the doc's rules
// ---------------------------------------------------------------------------

function annotateRoutes(workflow) {
  // Production-effective resources, so graph nodes agree with matchRoute and
  // the matched-route panel.
  const defaultResources = workflow?.default_route?.resources;
  return (workflow?.routes || []).map((route, index) => ({
    index,
    providers: route?.conditions?.providers || [],
    types: route?.conditions?.patient_types || [],
    flags: route?.conditions?.flags || [],
    resources: resolveMatchResources(route?.resources, defaultResources),
  }));
}

export function collectVisitTypes(workflow) {
  const set = new Set();
  for (const route of workflow?.routes || []) {
    for (const type of route?.conditions?.patient_types || []) set.add(type);
  }
  return [...set].sort((a, b) => a.localeCompare(b));
}

export function collectFlags(workflow) {
  const set = new Set();
  for (const route of workflow?.routes || []) {
    for (const flag of route?.conditions?.flags || []) set.add(flag);
  }
  return [...set].sort((a, b) => a.localeCompare(b));
}

// The top-layer block a route belongs to (mirrors groupIntoBlocks' keying).
export function blockKeyForRoute(route) {
  const types = route?.conditions?.patient_types || [];
  const flags = route?.conditions?.flags || [];
  if (types.length > 0) return "vt:" + [...types].sort().join("|");
  if (flags.length > 0) return "flag:" + [...flags].sort().join("|");
  return "any";
}

// Mirrors note_workflows._remove_whitespace_make_lowercase: production matches
// providers by this normalized display name, not by id.
export function normalizeProviderName(name) {
  if (name == null) return null;
  const cleaned = String(name).replace(/\s+/g, "").toLowerCase();
  return cleaned || null;
}

// True when the route has no provider condition or lists one whose normalized
// name matches, so ids sharing a display name match as they do in production.
export function routeProvidersMatch(routeProviders, providerName) {
  if (!routeProviders || routeProviders.length === 0) return true;
  const cleaned = normalizeProviderName(providerName);
  if (!cleaned) return false;
  return routeProviders.some(
    (p) => normalizeProviderName(p?.name) === cleaned
  );
}

// Prefer the spelling this specialty uses for a provider id; fall back to the
// caller's display name so distinct ids that share a name still resolve.
export function resolveProviderName(workflow, providerId, fallbackName = null) {
  if (providerId) {
    for (const route of workflow?.routes || []) {
      for (const provider of route?.conditions?.providers || []) {
        if (provider?.id === providerId && provider?.name) return provider.name;
      }
    }
  }
  return fallbackName || null;
}

// Single source of truth for the route predicate, shared by matchRoute and
// explainMatch so the two can't drift. Also returns the route's condition lists
// so callers can build reasons without re-reading conditions.
function evaluateRoute(conditions, { providerName = null, patientType = null, flagsSet }) {
  const routeProviders = conditions?.providers || [];
  const routeTypes = conditions?.patient_types || [];
  const routeFlags = conditions?.flags || [];
  const providerOk = routeProvidersMatch(routeProviders, providerName);
  const typeOk =
    routeTypes.length === 0 || (patientType && routeTypes.includes(patientType));
  const flagsOk = routeFlags.every((f) => flagsSet.has(f));
  return { providerOk, typeOk, flagsOk, routeProviders, routeTypes, routeFlags };
}

// Replicates the backend's pick_first_full_match: the first route whose
// provider, patient type, and flags all pass wins, else default_route. A sparse
// winner inherits default_route's resources (see resolveMatchResources).
export function matchRoute(
  workflow,
  { providerName = null, patientType = null, flags = [] } = {}
) {
  const flagsSet = new Set(flags);
  const routes = workflow?.routes || [];
  const defaultResources = workflow?.default_route?.resources;
  for (let i = 0; i < routes.length; i++) {
    const { providerOk, typeOk, flagsOk } = evaluateRoute(routes[i]?.conditions, {
      providerName,
      patientType,
      flagsSet,
    });
    if (providerOk && typeOk && flagsOk) {
      return {
        index: i,
        isDefault: false,
        route: routes[i],
        resources: resolveMatchResources(routes[i]?.resources, defaultResources),
      };
    }
  }
  return {
    index: null,
    isDefault: true,
    route: workflow?.default_route || null,
    resources: extractResources(defaultResources),
  };
}

// Step-by-step explanation of the match, for a "why this route?" trace. Routes
// for other providers are summarized as a count so the trace stays readable;
// the winner always agrees with matchRoute.
export function explainMatch(
  workflow,
  { providerName = null, patientType = null, flags = [] } = {}
) {
  const flagsSet = new Set(flags);
  const routes = workflow?.routes || [];
  const steps = [];
  let winner = null;
  let otherProviderCount = 0;

  for (let i = 0; i < routes.length; i++) {
    const { providerOk, typeOk, flagsOk, routeTypes, routeFlags } = evaluateRoute(
      routes[i]?.conditions,
      { providerName, patientType, flagsSet }
    );

    if (!providerOk) {
      otherProviderCount += 1;
      continue;
    }

    const reasons = [];
    if (!typeOk) {
      reasons.push(
        patientType
          ? `visit type "${patientType}" not in [${routeTypes.join(", ")}]`
          : `needs visit type [${routeTypes.join(", ")}]`
      );
    }
    if (!flagsOk) {
      const missing = routeFlags.filter((f) => !flagsSet.has(f));
      reasons.push(`missing flag${missing.length > 1 ? "s" : ""}: ${missing.join(", ")}`);
    }

    const passed = typeOk && flagsOk;
    if (passed && winner === null) {
      winner = i;
      steps.push({ index: i, route: routes[i], status: "matched", reasons: [] });
      break; // backend stops at first full match
    }
    steps.push({ index: i, route: routes[i], status: "skipped", reasons });
  }

  return { steps, winner, otherProviderCount };
}

function groupIntoBlocks(routes) {
  const blocks = new Map();
  const ensureBlock = (key, kind, label) => {
    if (!blocks.has(key)) {
      blocks.set(key, { key, kind, label, routes: [], firstIndex: Infinity });
    }
    return blocks.get(key);
  };

  for (const route of routes) {
    let block;
    if (route.types.length > 0) {
      // Rules 1, 2, 4, 7: visit type is the top layer. Multiple visit types on
      // one route form their own combined block (e.g. "FOLLOWUP / TELEMED").
      const key = "vt:" + [...route.types].sort().join("|");
      block = ensureBlock(key, "visit", route.types.join(" / "));
    } else if (route.flags.length > 0) {
      // Rules 5, 6: flag(+provider) routes get a top-layer flag block, placed
      // at the end of the visit-type list.
      const key = "flag:" + [...route.flags].sort().join("|");
      block = ensureBlock(key, "flag", route.flags.join(" + "));
    } else {
      // Rule 9: provider-only routes go under "Doctor-specific routes".
      block = ensureBlock("any", "any", "Doctor-specific routes");
    }
    block.routes.push(route);
    block.firstIndex = Math.min(block.firstIndex, route.index);
  }

  const byKind = (kind) =>
    [...blocks.values()]
      .filter((b) => b.kind === kind)
      .sort((a, b) => a.firstIndex - b.firstIndex);

  // Visit-type blocks first (route order), then "any visit type", flags last.
  return [...byKind("visit"), ...byKind("any"), ...byKind("flag")];
}

// Count by id, not name: display names repeat, so deduping on name would
// undercount the badge.
function uniqueProviderCount(routes) {
  const ids = new Set();
  for (const route of routes) {
    for (const provider of route.providers) ids.add(provider.id);
  }
  return ids.size;
}

// ---------------------------------------------------------------------------
// Tree construction (block children)
// ---------------------------------------------------------------------------

function makeRouteTreeNode(route, { highlighted = false, matchedRouteIndex = null } = {}) {
  const data = {
    routeIndex: route.index,
    anyProvider: route.providers.length === 0,
    // Keep id + name: names are not unique per workflow, so id is the stable
    // React key; name is for display.
    providers: route.providers.map((p) => ({ id: p.id, name: p.name })),
    resources: route.resources,
    highlighted,
    matched: matchedRouteIndex != null && route.index === matchedRouteIndex,
  };
  return {
    id: `route-${route.index}`,
    rfType: "routeNode",
    data,
    w: NODE_W.route,
    h: estimateRouteHeight(data),
    children: [],
  };
}

function listRowHeight(row) {
  const providerLines = row.anyProvider ? 1 : Math.max(1, row.providers.length);
  return 26 + providerLines * 16 + (row.resources.length ? row.resources.length * 20 : 0);
}

// Collapses a wide set of route children into one vertical list so blocks with
// many providers stay readable.
function makeListNode(block, routeChildren) {
  const rows = routeChildren.map((child) => child.data);
  const height = 44 + rows.reduce((sum, row) => sum + listRowHeight(row), 0);
  return {
    id: `${block.key}:list`,
    rfType: "listNode",
    data: { label: block.label, rows },
    w: NODE_W.list,
    h: height,
    children: [],
  };
}

// Builds a block's second-layer children. The first provider-less route in
// route order is the branch default: placed left-most and always highlighted.
function buildBlockChildren(block, matchedRouteIndex) {
  const anyProviderRoutes = block.routes.filter(
    (r) => r.providers.length === 0 && (block.kind !== "visit" || r.flags.length === 0)
  );
  const anyProviderSet = new Set(anyProviderRoutes);
  const rest = block.routes.filter((r) => !anyProviderSet.has(r));

  const fallbackIndex = anyProviderRoutes.length
    ? Math.min(...anyProviderRoutes.map((r) => r.index))
    : null;

  const children = anyProviderRoutes.map((route) =>
    makeRouteTreeNode(route, {
      highlighted: route.index === fallbackIndex,
      matchedRouteIndex,
    })
  );

  if (block.kind === "visit") {
    // Rules 4 & 7: visit type -> flag -> (provider or any-provider template).
    const flagChains = new Map();
    const plainEntries = [];
    for (const route of rest) {
      if (route.flags.length > 0) {
        const flagKey = [...route.flags].sort().join("|");
        if (!flagChains.has(flagKey)) {
          flagChains.set(flagKey, {
            flagKey,
            label: route.flags.join(" + "),
            routes: [],
            firstIndex: route.index,
          });
        }
        const chain = flagChains.get(flagKey);
        chain.routes.push(route);
        chain.firstIndex = Math.min(chain.firstIndex, route.index);
      } else {
        plainEntries.push({ firstIndex: route.index, route });
      }
    }

    const merged = [
      ...plainEntries,
      ...[...flagChains.values()].map((chain) => ({ firstIndex: chain.firstIndex, chain })),
    ].sort((a, b) => a.firstIndex - b.firstIndex);

    for (const entry of merged) {
      if (entry.route) {
        children.push(makeRouteTreeNode(entry.route, { matchedRouteIndex }));
      } else {
        const chain = entry.chain;
        const chainAnyProvider = chain.routes.filter((r) => r.providers.length === 0);
        const chainRest = chain.routes.filter((r) => r.providers.length > 0);
        const chainFallback = chainAnyProvider.length
          ? Math.min(...chainAnyProvider.map((r) => r.index))
          : null;
        children.push({
          id: `${block.key}:f:${chain.flagKey}`,
          rfType: "flagNode",
          data: { label: chain.label },
          w: NODE_W.flag,
          h: 46,
          children: [
            ...chainAnyProvider.map((route) =>
              makeRouteTreeNode(route, {
                highlighted: route.index === chainFallback,
                matchedRouteIndex,
              })
            ),
            ...chainRest
              .sort((a, b) => a.index - b.index)
              .map((route) => makeRouteTreeNode(route, { matchedRouteIndex })),
          ],
        });
      }
    }
  } else {
    // "any" and top-level flag blocks: remaining routes all list providers.
    for (const route of rest.sort((a, b) => a.index - b.index)) {
      children.push(makeRouteTreeNode(route, { matchedRouteIndex }));
    }
  }

  // Rule: several providers in one branch -> render as a vertical list.
  if (children.length >= LIST_MIN && children.every((c) => c.rfType === "routeNode")) {
    return [makeListNode(block, children)];
  }

  return children;
}

// ---------------------------------------------------------------------------
// Layout: deterministic layered tree layout (subtree-width based)
// ---------------------------------------------------------------------------

function layoutTree(root) {
  const maxHeightAtDepth = [];
  (function measure(node, depth) {
    maxHeightAtDepth[depth] = Math.max(maxHeightAtDepth[depth] || 0, node.h);
    node.children.forEach((child) => measure(child, depth + 1));
  })(root, 0);

  const yAtDepth = [0];
  for (let d = 1; d < maxHeightAtDepth.length; d++) {
    yAtDepth[d] = yAtDepth[d - 1] + maxHeightAtDepth[d - 1] + GAP_Y;
  }

  (function computeSubtreeWidth(node) {
    const childrenWidth =
      node.children.reduce((sum, child) => sum + computeSubtreeWidth(child), 0) +
      GAP_X * Math.max(0, node.children.length - 1);
    node.subW = Math.max(node.w, childrenWidth);
    return node.subW;
  })(root);

  const placements = [];
  (function place(node, left, depth) {
    const centerX = left + node.subW / 2;
    placements.push({ node, x: centerX - node.w / 2, y: yAtDepth[depth] });
    const childrenWidth =
      node.children.reduce((sum, child) => sum + child.subW, 0) +
      GAP_X * Math.max(0, node.children.length - 1);
    let childLeft = left + (node.subW - childrenWidth) / 2;
    for (const child of node.children) {
      place(child, childLeft, depth + 1);
      childLeft += child.subW + GAP_X;
    }
  })(root, 0, 0);

  return placements;
}

// ---------------------------------------------------------------------------
// Main entry
// ---------------------------------------------------------------------------

// Unique providers keyed by id for the dropdown, since display names repeat.
// Matching itself uses normalized names (see routeProvidersMatch).
export function collectProviders(workflow) {
  const byId = new Map();
  for (const route of workflow?.routes || []) {
    for (const provider of route?.conditions?.providers || []) {
      if (!byId.has(provider.id)) byId.set(provider.id, provider.name);
    }
  }
  return [...byId.entries()]
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * @param {object} workflow one entry of workflows.json's `workflows` array
 * @param {object} options
 * @param {Set<string>} options.expandedKeys block keys the user expanded
 * @param {Set<string>} options.collapsedKeys block keys the user collapsed (wins
 *   over auto-expand)
 * @param {string|null} options.selectedProviderName provider display name used
 *   to filter visible routes (normalized name compare, like production)
 * @param {Set<string>} options.highlightBlockKeys blocks to visually emphasize
 * @param {number|null} options.matchedRouteIndex route index to mark as matched
 * @returns {{ nodes: any[], edges: any[], blockKeys: string[] }}
 */
export function buildGraph(
  workflow,
  {
    expandedKeys = new Set(),
    collapsedKeys = new Set(),
    selectedProviderName = null,
    highlightBlockKeys = new Set(),
    matchedRouteIndex = null,
  } = {}
) {
  const allRoutes = annotateRoutes(workflow);

  // A route can apply to the selected provider if it lists them (by normalized
  // name) or has no provider condition (wildcard).
  const visibleRoutes = selectedProviderName
    ? allRoutes.filter((r) => routeProvidersMatch(r.providers, selectedProviderName))
    : allRoutes;

  const blocks = groupIntoBlocks(visibleRoutes);
  const blockKeys = blocks.map((b) => b.key);

  const root = {
    id: "specialty",
    rfType: "specialtyNode",
    data: { specialty: workflow?.specialty || "", routeCount: allRoutes.length },
    w: NODE_W.specialty,
    h: 64,
    children: [],
  };

  const edges = [];
  for (const block of blocks) {
    // Base auto-expand (provider filter or condition highlight), overridable by
    // the user's explicit expand/collapse of a block.
    const autoExpanded =
      Boolean(selectedProviderName) || highlightBlockKeys.has(block.key);
    const expanded =
      (expandedKeys.has(block.key) || autoExpanded) && !collapsedKeys.has(block.key);
    const blockNode = {
      id: block.key,
      rfType: "groupNode",
      data: {
        kind: block.kind,
        label: block.label,
        routeCount: block.routes.length,
        providerCount: uniqueProviderCount(block.routes),
        expanded,
        highlighted: highlightBlockKeys.has(block.key),
      },
      w: NODE_W.group,
      h: 78,
      children: expanded ? buildBlockChildren(block, matchedRouteIndex) : [],
    };
    root.children.push(blockNode);
    // Specialty node has two source handles (bottom + right); pin block edges to
    // the bottom one so they don't depend on handle ordering.
    edges.push(makeEdge("specialty", block.key, { sourceHandle: "bottom" }));

    for (const child of blockNode.children) {
      edges.push(makeEdge(block.key, child.id));
      for (const grandChild of child.children) {
        edges.push(makeEdge(child.id, grandChild.id));
      }
    }
  }

  const placements = layoutTree(root);
  const nodes = placements.map(({ node, x, y }) => ({
    id: node.id,
    type: node.rfType,
    position: { x, y },
    data: node.data,
    style: { width: node.w },
    draggable: false,
    connectable: false,
  }));

  // Rule 10: default route at the very top, next to the specialty root. Only
  // when the workflow defines one, so we never draw an empty box with an edge
  // pointing at nothing.
  if (workflow?.default_route) {
    const defaultResources = extractResources(workflow.default_route.resources);
    const rootPlacement = placements.find((p) => p.node.id === "specialty");
    nodes.push({
      id: "default-route",
      type: "defaultNode",
      position: {
        x: rootPlacement.x + NODE_W.specialty + 90,
        y: rootPlacement.y,
      },
      data: { resources: defaultResources },
      style: { width: NODE_W.default, minHeight: estimateDefaultHeight(defaultResources) },
      draggable: false,
      connectable: false,
    });
    edges.push({
      id: "e-specialty-default",
      source: "specialty",
      target: "default-route",
      sourceHandle: "right",
      targetHandle: "left",
      type: "smoothstep",
      label: "no route matches",
      labelStyle: { fontSize: 10, fill: "#4338ca" },
      labelBgStyle: { fill: "#eef2ff" },
      style: { stroke: "#4f46e5", strokeWidth: 1.5, strokeDasharray: "6 4" },
      markerEnd: { type: "arrowclosed", color: "#4f46e5", width: 18, height: 18 },
    });
  }

  return { nodes, edges, blockKeys };
}

function makeEdge(source, target, { sourceHandle } = {}) {
  return {
    id: `e-${source}-${target}`,
    source,
    target,
    ...(sourceHandle ? { sourceHandle } : {}),
    type: "smoothstep",
    style: { stroke: "#9ca3af", strokeWidth: 1.5 },
    markerEnd: { type: "arrowclosed", color: "#9ca3af", width: 18, height: 18 },
  };
}
